import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isActivePromotion, useClusterDetail, useFleetOverview, usePromotions, useRefreshMutation, useTree } from "../api/queries";
import type { ArgoTsVersionState, Me, PromotionRequest } from "../api/types";
import DataAgeBadge from "../components/DataAgeBadge";
import MrChip from "../components/MrChip";
import Spinner from "../components/Spinner";
import VersionChip from "../components/VersionChip";
import { findEnvCluster, mergeOverviews } from "../util/argots";
import { formatDateTime, relativeAge } from "../util/format";
import { showToast } from "../util/toast";
import { behindLatest } from "../util/versions";

export default function EnvDetailDrawer({ envKey, me }: { envKey: string; me: Me }) {
    const navigate = useNavigate();
    const location = useLocation();
    const treeQ = useTree();
    const overviewQ = useFleetOverview("argots");
    const coreOverviewQ = useFleetOverview("core");
    const promosQ = usePromotions("all");
    const refresh = useRefreshMutation();

    const tree = treeQ.data ?? null;
    const overview = mergeOverviews(overviewQ.data ?? null, coreOverviewQ.data ?? null);
    const env = tree?.config?.envs[envKey];
    const pin = tree?.pins[envKey];
    const latestTag = overview?.latestTags?.components["argocd-ts-cmp"]?.[0];
    const cluster = findEnvCluster(overview, envKey, env);
    const detailQ = useClusterDetail(cluster ? cluster.cluster : null);
    const state = detailQ.data?.version?.state;
    const argo: ArgoTsVersionState | null = state && state.fleet === "argots" ? state : null;

    const close = () => navigate({ pathname: "/", search: location.search });

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") navigate({ pathname: "/", search: location.search });
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [navigate, location.search]);

    const promos = (promosQ.data ?? []).filter((p) => p.envKey === envKey).sort((a, b) => (a.requestedAt < b.requestedAt ? 1 : -1));
    const inFlight = promos.filter(isActivePromotion);
    const recent = promos.filter((p) => !isActivePromotion(p)).slice(0, 10);

    const sources: { label: string; url: string }[] = [];
    const coreArgots = state && state.fleet === "core" ? state.argots : undefined;
    const infraUrl = argo?.infraVersion?.sourceUrl ?? coreArgots?.sourceUrl;
    if (infraUrl) sources.push({ label: "infra (k8s-clusters)", url: infraUrl });
    if (argo?.appInfraVersion?.sourceUrl) sources.push({ label: "app infra (app_clusters)", url: argo.appInfraVersion.sourceUrl });
    if (argo?.argoApp?.sourceUrl) sources.push({ label: "Argo Application", url: argo.argoApp.sourceUrl });

    const doRefresh = () => {
        if (!cluster) return;
        refresh.mutate(`cluster:${cluster.cluster}`, {
            onSuccess: () => showToast({ kind: "success", message: `Refresh queued for ${cluster.cluster}` }),
            onError: (err) => showToast({ kind: "error", message: err instanceof Error ? err.message : "Refresh failed" }),
        });
    };

    return (
        <div className="drawer-backdrop" onClick={close}>
            <aside className="drawer" onClick={(e) => e.stopPropagation()}>
                <header className="drawer-header">
                    <h2>{envKey}</h2>
                    {cluster ? <span className="muted">{cluster.cluster}</span> : null}
                    <button className="icon-btn" onClick={close} aria-label="Close">
                        ×
                    </button>
                </header>

                {treeQ.isPending || overviewQ.isPending ? <Spinner /> : null}

                <section>
                    <h3>Version</h3>
                    <div className="drawer-row">
                        <span className="muted">pinned</span>
                        <VersionChip version={pin?.version} behind={behindLatest(pin?.version, latestTag)} />
                        {pin?.landedAt ? <span className="muted">landed {relativeAge(pin.landedAt)}</span> : null}
                    </div>
                    <div className="drawer-row">
                        <span className="muted">latest argocd-ts-cmp</span>
                        <VersionChip version={latestTag} />
                    </div>
                    {cluster ? (
                        <div className="drawer-row">
                            <span className="muted">data age</span>
                            <DataAgeBadge fetchedAt={cluster.fetchedAt} />
                        </div>
                    ) : null}
                </section>

                <section>
                    <h3>Promotion config</h3>
                    {env ? (
                        <dl className="config-grid">
                            <dt>dependsOn</dt>
                            <dd>{env.dependsOn ?? "null (canary head)"}</dd>
                            <dt>soakDays</dt>
                            <dd>{env.soakDays ?? "—"}</dd>
                            <dt>windows</dt>
                            <dd>{env.windows && env.windows.length > 0 ? env.windows.join(", ") : "—"}</dd>
                            <dt>mergePolicy</dt>
                            <dd>{env.mergePolicy}</dd>
                            <dt>delaySeconds</dt>
                            <dd>{env.delaySeconds ?? "—"}</dd>
                        </dl>
                    ) : (
                        <p className="muted">env not found in promotion config</p>
                    )}
                </section>

                <section>
                    <h3>Promotions</h3>
                    {inFlight.length > 0 ? inFlight.map((p) => <PromoRow key={p.id} p={p} />) : <p className="muted">none in flight</p>}
                    {recent.length > 0 ? (
                        <>
                            <h4 className="muted">recent</h4>
                            {recent.map((p) => (
                                <PromoRow key={p.id} p={p} />
                            ))}
                        </>
                    ) : null}
                </section>

                <section>
                    <h3>Sources</h3>
                    {sources.length > 0 ? (
                        sources.map((s) => (
                            <div key={s.label}>
                                <a className="source-link" href={s.url} target="_blank" rel="noopener noreferrer">
                                    {s.label}
                                </a>
                            </div>
                        ))
                    ) : (
                        <p className="muted">{cluster && detailQ.isPending ? "loading…" : "no source links"}</p>
                    )}
                    {cluster?.grafanaUrl ? (
                        <div>
                            <a className="source-link" href={cluster.grafanaUrl} target="_blank" rel="noopener noreferrer">
                                Grafana
                            </a>
                        </div>
                    ) : null}
                </section>

                {me.role === "promoter" && cluster ? (
                    <section>
                        <button className="btn" disabled={refresh.isPending} onClick={doRefresh}>
                            {refresh.isPending ? "Queueing…" : "Refresh now"}
                        </button>
                    </section>
                ) : null}
            </aside>
        </div>
    );
}

function PromoRow({ p }: { p: PromotionRequest }) {
    return (
        <div className="promo-row">
            <MrChip promotion={p} />
            <span className="version">
                {p.fromVersion ?? "?"} → {p.toVersion ?? p.requestedVersion ?? "upstream"}
            </span>
            <span className="muted">
                {p.requestedBy} · {formatDateTime(p.requestedAt)}
            </span>
        </div>
    );
}
